import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { graphql } from '@/gql';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataGrid } from '@/components/ui/DataGrid';

const RolesDoc = graphql(/* GraphQL */ `
  query RolesList {
    roles {
      id
      name
      slug
      description
      actorKind
      isSystem
      permissions {
        code
        requiresStrongAuth
      }
    }
  }
`);

type RoleRow = {
  id: string;
  name: string;
  slug: string;
  description: string;
  actorKind: string;
  isSystem: boolean;
  permissionCount: number;
  strongAuthCount: number;
};

export function RolesListPage() {
  const navigate = useNavigate();
  const { data, loading } = useQuery(RolesDoc, {
    fetchPolicy: 'network-only',
  });

  const rows: RoleRow[] = useMemo(
    () =>
      (data?.roles ?? []).map((r) => ({
        id: r.id,
        name: r.name,
        slug: r.slug,
        description: r.description,
        actorKind: r.actorKind,
        isSystem: r.isSystem,
        permissionCount: r.permissions.length,
        strongAuthCount: r.permissions.filter((p) => p.requiresStrongAuth).length,
      })),
    [data],
  );

  const columns = useMemo<ColumnDef<RoleRow>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to={`/roles/${row.original.id}`}
            className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
          >
            {row.original.name}
          </Link>
        ),
      },
      { accessorKey: 'slug', header: 'Slug' },
      {
        accessorKey: 'actorKind',
        header: 'Actor',
        cell: ({ getValue }) => (
          <Badge tone="neutral">{String(getValue())}</Badge>
        ),
      },
      {
        accessorKey: 'isSystem',
        header: 'System',
        cell: ({ getValue }) =>
          getValue() ? <Badge tone="warning">System</Badge> : null,
      },
      {
        accessorKey: 'permissionCount',
        header: 'Permissions',
        cell: ({ row }) => (
          <span>
            {row.original.permissionCount}
            {row.original.strongAuthCount > 0 ? (
              <Badge tone="warning" className="ml-2">
                {row.original.strongAuthCount} passkey
              </Badge>
            ) : null}
          </span>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
          Roles
        </h1>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => navigate('/roles/new')}>
          New role
        </Button>
      </div>
      <DataGrid
        columns={columns}
        data={rows}
        rowId={(row) => row.id}
        loading={loading}
      />
    </div>
  );
}
